import { useState } from "react";
import { Cpu, Info, Zap, ShieldCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

function scoreColor(score) {
  if (score >= 85) return { bg: "bg-[#0033A0]", text: "text-[#0033A0]", ring: "ring-[#0033A0]/25", label: "Elite" };
  if (score >= 75) return { bg: "bg-[#0F9B7F]", text: "text-[#0F9B7F]", ring: "ring-[#0F9B7F]/25", label: "AI Ready" };
  if (score >= 60) return { bg: "bg-[#F59E0B]", text: "text-[#B45309]", ring: "ring-[#F59E0B]/25", label: "Capable" };
  if (score >= 40) return { bg: "bg-slate-400", text: "text-slate-600", ring: "ring-slate-200", label: "Basic" };
  return { bg: "bg-slate-300", text: "text-slate-500", ring: "ring-slate-200", label: "Non-AI" };
}

function DotBar({ value, color }) {
  // 5-dot indicator
  const filled = Math.round((value / 100) * 5);
  return (
    <div className="flex gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < filled ? color : "bg-slate-200"}`} />
      ))}
    </div>
  );
}

/**
 * Compact AI Relevance Score chip embedded in each catalogue card.
 * `data` is the relevance object from GET /api/catalogue/relevance.
 */
export default function RelevanceScore({ data, personaName }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const c = scoreColor(data.score);

  return (
    <div className="mt-2 border border-slate-200 rounded-md p-2.5 bg-slate-50/60" data-testid={`relevance-score-${data.device_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-8 h-8 shrink-0 rounded-md ${c.bg} text-white grid place-items-center font-black text-[13px]`}>
            {data.score}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`text-[11px] font-bold uppercase tracking-wider ${c.text}`}>{data.label}</span>
              {data.profile?.copilot_plus_ready && (
                <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px] px-1.5 py-0 h-4">Copilot+</Badge>
              )}
            </div>
            <div className="text-[10px] text-slate-500 leading-tight truncate">
              Relevance for {personaName}
            </div>
          </div>
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid={`relevance-info-${data.device_id}`}
              className="text-slate-400 hover:text-[#0033A0] transition-colors"
              aria-label="Why this score?"
            >
              <Info size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-4 bg-white" data-testid={`relevance-popover-${data.device_id}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Why this score?</div>
            <div className="font-display font-bold text-slate-900 text-sm mt-0.5">
              {data.score} / 100 · <span className={c.text}>{data.label}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Persona: {personaName}</div>

            <div className="mt-3 space-y-2">
              {data.breakdown.map((b) => (
                <div key={b.label}>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-700">{b.label} <span className="text-slate-400">· {b.detail}</span></span>
                    <span className="font-mono text-slate-900 font-semibold">{b.value}</span>
                  </div>
                  <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${c.bg}`}
                      style={{ width: `${b.value}%`, opacity: 0.4 + b.weight }}
                    />
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">Weight: {Math.round(b.weight * 100)}%</div>
                </div>
              ))}
            </div>

            {data.matched_apps?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  <Zap size={10} className="text-[#0033A0]" /> Runs great
                </div>
                <div className="mt-1.5 space-y-1">
                  {data.matched_apps.map((a) => (
                    <div key={a.id} className="text-[11px] text-slate-700 flex items-center justify-between">
                      <span className="truncate">{a.name}</span>
                      <span className="text-[9px] text-slate-400 shrink-0 ml-2">{a.npu_share}% NPU</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] text-slate-500 min-w-0">
          <span className="flex items-center gap-0.5"><Cpu size={9} /> {data.profile?.npu_tops || 0} TOPS</span>
          <span className="text-slate-300">·</span>
          <span className="truncate">{data.matched_apps?.length || 0} AI apps ready</span>
        </div>
        <DotBar value={data.score} color={c.bg} />
      </div>
    </div>
  );
}

export { scoreColor };
